import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { errorToastify } from "../../utils/toastify.util";

// ✅ from your userSlice
import { getUserList } from "../../features/userSlice";

// ✅ from subscriptionSlice
import {
  fetchSubscriptionMappedUsers,
  mapUsersToSubscription,
} from "../../features/subscriptionSlice";

/** ✅ Searchable Multi-select + Done button (closes list) */
const MultiSelectUsers = ({
  label,
  placeholder = "Search user...",
  options = [],
  selectedIds = [],
  onToggle,
  onDone,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const selectedText = useMemo(() => {
    const count = selectedIds.length || 0;
    return count ? `${count} Selected` : `Select ${label}`;
  }, [selectedIds, label]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return options;

    return options.filter((u) => {
      const name = String(u?.name || "").toLowerCase();
      const email = String(u?.email || u?.username || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [options, search]);

  

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="mb-3" ref={wrapRef}>
      <label className="form-label fs-7">{label}</label>

      <div className="category-dd">
        <div
          className={`form-control category-dd-input ${disabled ? "bg-light" : ""}`}
          role="button"
          style={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.7 : 1,
          }}
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
        >
          <span>{selectedText}</span>
          <span className="category-dd-arrow">{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div className="category-dd-panel">
            <input
              type="text"
              className="form-control mb-2"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="category-dd-list">
              {filtered.length === 0 ? (
                <div className="text-muted small">No users found</div>
              ) : (
                <div className="category-dd-grid">
                  {filtered.map((u) => (
                    <label key={u.id} className="category-dd-item">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(Number(u.id))}
                        onChange={() => onToggle(Number(u.id))}
                      />
                      <span>
                        {u.name || "User"}{" "}
                        <small className="text-muted">
                          ({u.email || u.username || ""})
                        </small>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* ✅ DONE button */}
            <div className="category-dd-footer">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setOpen(false);
                  onDone && onDone();
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SubscriptionUserMapModal = ({ closeModal, subscriptionId }) => {
  const dispatch = useDispatch();

  // ✅ user list from your userSlice
  const { userList, isLoading: userLoading } = useSelector((s) => s.user);

  // ✅ mapped user ids from subscription slice
  // (use whichever flags you created: isMapUsersLoad/isMapUsersBtnLoad, etc.)
  const { mappedUserIds, isMapUsersBtnLoad } = useSelector((s) => s.subscription);

  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const LIMIT_ALL = 100;

  // ✅ 1) when modal opens: fetch mapped users + users list
  useEffect(() => {
    if (!subscriptionId) return;

    dispatch(fetchSubscriptionMappedUsers({ subscriptionid: subscriptionId }));

    dispatch(
      getUserList({
        limit: LIMIT_ALL,
        offset: 0,
        searchText: "",
        usertype: "user",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId]);

  // ✅ 2) when mappedUserIds arrives => preselect
  useEffect(() => {
    const arr = Array.isArray(mappedUserIds) ? mappedUserIds : [];
    setSelectedUserIds(arr.map(Number).filter(Boolean));
  }, [mappedUserIds]);

  const options = useMemo(() => {
  const list = Array.isArray(userList) ? userList : [];

  const mapped = list.map((u) => ({
    id: Number(u.id),
    name: u.name || u.fullname || u.username || "User",
    email: u.email || u.username || "",
  }));

  const selectedSet = new Set((selectedUserIds || []).map(Number));

  // ✅ selected first, then by name
  mapped.sort((a, b) => {
    const aSel = selectedSet.has(a.id) ? 1 : 0;
    const bSel = selectedSet.has(b.id) ? 1 : 0;
    if (aSel !== bSel) return bSel - aSel;

    return String(a.name).localeCompare(String(b.name));
  });

  return mapped;
}, [userList, selectedUserIds]);

  const toggleUser = (id) => {
    const n = Number(id);
    if (!n) return;

    setSelectedUserIds((prev) => {
      const exists = prev.includes(n);
      return exists ? prev.filter((x) => x !== n) : [...prev, n];
    });
  };

  const onSubmit = async () => {
    if (!subscriptionId) return errorToastify("Subscription id missing");
    if (!selectedUserIds.length) return errorToastify("Please select users");

    const payload = {
      subscriptionid: Number(subscriptionId),
      userids: selectedUserIds.map(Number),
    };

    const res = await dispatch(mapUsersToSubscription(payload));
    if (res?.payload?.success || res?.payload?.status === 1) {
      // successToastify(res?.payload?.message || "Users mapped successfully");
      closeModal && closeModal();
    }
  };


  

  return (
    <div className="container">
      {/* ✅ Multi select only (search stays inside dropdown) */}
      <MultiSelectUsers
        label="Users"
        placeholder="Search users..."
        options={options}
        selectedIds={selectedUserIds}
        onToggle={toggleUser}
        onDone={() => {}}
        disabled={userLoading}
      />

      {/* submit */}
      <div className="mt-3">
        <button
          type="button"
          className="btn btn-primary w-100"
          disabled={isMapUsersBtnLoad}
          onClick={onSubmit}
        >
          {isMapUsersBtnLoad ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              Loading...
            </>
          ) : (
            <>Submit Mapping</>
          )}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionUserMapModal;